/**
 * features/finance/FeeCaseApproval.tsx
 * ------------------------------------------------------------------
 * The approval control shared by every fee special case — staff children,
 * full annual payment, bursaries.
 *
 * ONE COMPONENT, because the rule is one rule. Whoever arranges a special
 * case must not be the one who approves it: the bursar proposes, the Head
 * decides. A bursary is money off a bill, and the same reasoning that keeps
 * a payment voucher's preparer away from its approval applies here.
 *
 * It renders the case's STATE to everyone and the DECISION only to an
 * approver. That split is deliberate: a bursar needs to see that the award
 * they raised is still waiting — a screen that simply hides the whole
 * apparatus from them leaves them wondering whether it saved.
 *
 * This is presentation. The rule it describes is enforced twice behind it:
 * in Firestore rules (a finance manager's write can never produce an
 * approved document) and at the billing feed (only an approved case is fed
 * into a billing run). Neither depends on this component being rendered.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Check, X, Clock, Ban } from 'lucide-react';
import type { FeeCaseStatus } from '@/lib/financeCore';

/** The state badge — shown to anyone who can see the case at all. */
export function FeeCaseBadge({
  status,
  approvedByName,
  rejectedReason,
}: {
  status?: FeeCaseStatus;
  approvedByName?: string;
  rejectedReason?: string;
}) {
  // A record written before `status` existed reads as pending, never as
  // approved. The safe default when nobody knows who approved it is nobody.
  const s: FeeCaseStatus = status ?? 'pending';

  if (s === 'approved') {
    return (
      <span
        title={approvedByName ? `Approved by ${approvedByName}` : 'Approved'}
        className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 inline-flex items-center gap-1"
      >
        <Check size={11} /> Approved
      </span>
    );
  }
  if (s === 'rejected') {
    return (
      <span
        title={rejectedReason || 'Rejected'}
        className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 inline-flex items-center gap-1"
      >
        <Ban size={11} /> Rejected
      </span>
    );
  }
  return (
    <span
      title="Waiting for approval — this case does not affect billing yet"
      className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 inline-flex items-center gap-1"
    >
      <Clock size={11} /> Pending
    </span>
  );
}

/**
 * Approve / reject buttons. Render only for a user who holds
 * canApproveFeeCases — the caller decides that, so this component stays
 * ignorant of the permission model.
 */
export function FeeCaseDecision({
  status,
  busy = false,
  onApprove,
  onReject,
}: {
  status?: FeeCaseStatus;
  busy?: boolean;
  onApprove: () => void;
  onReject: (reason: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const s: FeeCaseStatus = status ?? 'pending';

  if (asking) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (kept on the record)"
          className="px-2 py-1 text-xs rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 w-56"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && reason.trim()) { onReject(reason.trim()); setAsking(false); setReason(''); }
            if (e.key === 'Escape') { setAsking(false); setReason(''); }
          }}
        />
        <button
          type="button"
          disabled={busy || !reason.trim()}
          onClick={() => { onReject(reason.trim()); setAsking(false); setReason(''); }}
          className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-600 text-white disabled:opacity-40"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => { setAsking(false); setReason(''); }}
          className="p-1 text-gray-400 hover:text-gray-600"
          title="Cancel"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {s !== 'approved' && (
        <button
          type="button"
          disabled={busy}
          onClick={onApprove}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1"
        >
          <Check size={12} /> Approve
        </button>
      )}
      {s !== 'rejected' && (
        <button
          type="button"
          disabled={busy}
          /* A rejection always carries a reason. "Rejected" with no note is a
             decision nobody can revisit six months later. */
          onClick={() => setAsking(true)}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 disabled:opacity-40 inline-flex items-center gap-1"
        >
          <Ban size={12} /> Reject
        </button>
      )}
    </div>
  );
}

/**
 * The fields to write when a decision is taken. Kept here so all three case
 * types record a decision identically — an approval that stores a different
 * shape per screen is one an audit cannot read across.
 */
export function approvalPatch(
  decision: 'approved' | 'rejected',
  actor: { uid?: string; displayName?: string } | null,
  reason?: string,
) {
  return {
    status: decision,
    approvedBy: actor?.uid,
    approvedByName: actor?.displayName,
    approvedAt: new Date().toISOString(),
    // Cleared on approval so a re-approved case does not keep explaining why
    // it was once turned down.
    rejectedReason: decision === 'rejected' ? (reason || 'No reason given') : undefined,
  };
}
